import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(true);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true) {
      setIsInstalled(true);
      setShowPrompt(false);
      return;
    }

    // Capture the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Handle successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  // Only show if prompt is available
  if (isInstalled || !showPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 max-w-sm z-30 animate-slide-up">
      <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl shadow-2xl border border-primary/30 overflow-hidden">
        {/* Header */}
        <div className="p-4 bg-primary/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
              <img
                src="/icon_256x256.png"
                alt="Alkane Engineering"
                className="w-10 h-10 object-contain"
              />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-bold text-sm">
                Download Alkane App
              </h3>
              <p className="text-white/80 text-xs">
                Get quick access to our services
              </p>
            </div>
            <button
              onClick={() => setShowPrompt(false)}
              className="text-white/60 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 border-t border-primary/20">
          <p className="text-white/90 text-xs mb-3">
            Works offline. Quick access from home screen. App-like experience.
          </p>

          {/* Action Button */}
          <button
            onClick={handleInstall}
            className="w-full bg-white text-primary px-4 py-2 rounded-lg font-semibold text-sm transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95 flex items-center justify-center gap-2"
          >
            <Download size={16} />
            Install Now
          </button>
        </div>
      </div>
    </div>
  );
}
